import { httpClient } from '@/lib/httpClient';

export type TicketStatus = 'open' | 'atendimento' | 'finalized';
export type TicketPriority = 'low' | 'high' | 'critical';
export type TicketAuthorRole = 'admin' | 'root';

export interface TicketAttachmentPayload {
  storageKey: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  width?: number;
  height?: number;
}

export interface TicketAttachment extends TicketAttachmentPayload {
  id: number;
  ticketMessageId: number;
  createdAt: string;
}

export interface TicketMessage {
  id: number;
  ticketId: number;
  authorUserId: number;
  authorRole: TicketAuthorRole;
  authorName?: string;
  authorFirstName?: string;
  authorLastName?: string;
  adminName?: string;
  customerUserName?: string;
  createdByName?: string;
  supportName?: string;
  author?: {
    id?: number;
    firstName?: string;
    lastName?: string;
    name?: string;
    fullName?: string;
    displayName?: string;
  };
  user?: {
    id?: number;
    firstName?: string;
    lastName?: string;
    name?: string;
    fullName?: string;
    displayName?: string;
  };
  messageText: string | null;
  createdAt: string;
  attachments: TicketAttachment[];
}

export interface TicketSummary {
  id: number;
  subject: string;
  status: TicketStatus;
  priority: TicketPriority;
  initialPriority: TicketPriority;
  priorityUpdatedByUserId: number | null;
  priorityUpdatedAt: string | null;
  priorityUpdatedBy?: {
    id?: number;
    firstName?: string;
    lastName?: string;
    name?: string;
    fullName?: string;
    displayName?: string;
  } | null;
  awaitingRootReply: boolean;
  createdByUserId: number;
  createdBy?: {
    id?: number;
    firstName?: string;
    lastName?: string;
    name?: string;
    fullName?: string;
    displayName?: string;
    photo?: string | null;
    deletedAt?: string | null;
  };
  createdByName?: string;
  createdByFirstName?: string;
  createdByLastName?: string;
  createdByUserName?: string;
  openerName?: string;
  requesterName?: string;
  createdByUser?: {
    id?: number;
    firstName?: string;
    lastName?: string;
    name?: string;
    fullName?: string;
    displayName?: string;
  };
  customerId: number;
  customerName?: string;
  customer?: {
    id?: number;
    name?: string;
    displayName?: string;
    fullName?: string;
    tradeName?: string;
    legalName?: string;
  };
  createdAt: string;
  updatedAt: string;
  finalizedAt: string | null;
}

export interface TicketDetail extends TicketSummary {
  finalizedByUserId: number | null;
  createdByName?: string;
  createdByFirstName?: string;
  createdByLastName?: string;
  createdByUserName?: string;
  openerName?: string;
  requesterName?: string;
  createdByUser?: {
    id?: number;
    firstName?: string;
    lastName?: string;
    name?: string;
    fullName?: string;
    displayName?: string;
  };
  messages: TicketMessage[];
}

export interface TicketListQuery {
  status?: TicketStatus;
  priority?: TicketPriority;
  subject?: string;
  description?: string;
  customerId?: number;
  limit?: number;
  offset?: number;
}

export interface TicketListResponse {
  data: TicketSummary[];
  total: number;
  limit: number;
  offset: number;
}

export interface CreateTicketPayload {
  subject: string;
  message?: string;
  priority?: TicketPriority;
  attachments?: TicketAttachmentPayload[];
}

export interface UpdateTicketPriorityPayload {
  priority: TicketPriority;
}

export interface SendTicketMessagePayload {
  message?: string;
  attachments?: TicketAttachmentPayload[];
}

export interface TicketUploadPresignPayload {
  fileName: string;
  contentType: string;
  fileSizeBytes: number;
}

export interface TicketUploadPresignResponse {
  storageKey: string;
  uploadUrl: string;
  expiresIn: number;
  maxFileSizeBytes: number;
  allowedMimeTypes: string[];
}

export interface TicketAttachmentDownloadUrlResponse {
  downloadUrl: string;
  expiresIn: number;
}

class TicketsService {
  async listTickets(query: TicketListQuery) {
    const { data } = await httpClient.get<TicketListResponse>('/v1/tickets', {
      params: query,
    });

    return data;
  }

  async getTicketDetail(ticketId: number) {
    const { data } = await httpClient.get<TicketDetail>(
      `/v1/tickets/${ticketId}`,
    );

    return data;
  }

  async createTicket(payload: CreateTicketPayload) {
    const { data } = await httpClient.post<TicketDetail>(
      '/v1/tickets',
      payload,
    );

    return data;
  }

  async sendMessage(ticketId: number, payload: SendTicketMessagePayload) {
    const { data } = await httpClient.post<TicketDetail>(
      `/v1/tickets/${ticketId}/messages`,
      payload,
    );

    return data;
  }

  async finalizeTicket(ticketId: number) {
    const { data } = await httpClient.patch<TicketDetail>(
      `/v1/tickets/${ticketId}/finalize`,
    );

    return data;
  }

  async reopenTicket(ticketId: number) {
    const { data } = await httpClient.patch<TicketDetail>(
      `/v1/tickets/${ticketId}/reopen`,
    );

    return data;
  }

  async updateTicketPriority(
    ticketId: number,
    payload: UpdateTicketPriorityPayload,
  ) {
    const { data } = await httpClient.patch<TicketDetail>(
      `/v1/tickets/${ticketId}/priority`,
      payload,
    );

    return data;
  }

  async generateUploadPresign(payload: TicketUploadPresignPayload) {
    const { data } = await httpClient.post<TicketUploadPresignResponse>(
      '/v1/tickets/uploads/presign',
      payload,
    );

    return data;
  }

  async getAttachmentDownloadUrl(attachmentId: number) {
    const { data } = await httpClient.get<TicketAttachmentDownloadUrlResponse>(
      `/v1/tickets/attachments/${attachmentId}/download-url`,
    );

    return data;
  }
}

export default new TicketsService();
